import { Link } from 'react-router';
import { cn } from "@/lib/utils";
import type { LucideIcon } from "lucide-react";

export interface NavItem {
  key: string;
  label: string;
  href?: string;
  icon: LucideIcon;
  badge?: number | string;
  badgeTone?: "brand" | "amber" | "rose" | "blue" | "slate";
  group?: "analytics" | "manage";
}

const badgeToneMap = {
  brand: "bg-brand-600 text-white",
  amber: "bg-amber-500 text-white",
  rose:  "bg-rose-600  text-white",
  blue:  "bg-blue-600  text-white",
  slate: "bg-slate-500 text-white",
};

export function NavSidebar({
  items,
  activeKey,
  onSelect,
  header,
  footer,
  className,
}: {
  items: NavItem[];
  activeKey?: string;
  onSelect?: (key: string) => void;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
}) {
  const analytics = items.filter((i) => i.group === "analytics");
  const manage    = items.filter((i) => i.group === "manage");

  const renderGroup = (title: string, list: NavItem[]) => (
    <div>
      <p className="eyebrow px-3 mb-2">{title}</p>
      <ul className="flex flex-col gap-0.5">
        {list.map((item) => {
          const isActive = item.key === activeKey;
          const Icon = item.icon;

          const content = (
            <span className={cn(
              "flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition focus-ring",
              isActive
                ? "bg-brand-50 text-brand-800"
                : "text-muted-foreground hover:bg-muted/40 hover:text-foreground"
            )}>
              <Icon className={cn("h-4 w-4 shrink-0", isActive ? "text-brand-700" : "text-muted-foreground")} />
              <span className="flex-1 truncate">{item.label}</span>
              {item.badge !== undefined && item.badge !== null && item.badge !== 0 && (
                <span className={cn(
                  "inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold",
                  badgeToneMap[item.badgeTone ?? "slate"]
                )}>
                  {item.badge}
                </span>
              )}
            </span>
          );

          return (
            <li key={item.key}>
              {item.href ? (
                <Link to={item.href} className="block">{content}</Link>
              ) : (
                <button
                  type="button"
                  onClick={() => onSelect?.(item.key)}
                  className="block w-full text-left"
                >
                  {content}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );

  return (
    <aside
      className={cn(
        "hidden lg:flex lg:sticky lg:top-4 lg:h-[calc(100vh-2rem)]",
        "w-60 shrink-0 flex-col rounded-2xl border border-border bg-card shadow-card",
        className
      )}
      aria-label="Primary"
    >
      {header && <div className="p-4 border-b border-border/50">{header}</div>}
      <nav className="flex-1 overflow-y-auto p-3 flex flex-col gap-5">
        {analytics.length > 0 && renderGroup("Analytics", analytics)}
        {manage.length   > 0 && renderGroup("Manage",    manage)}
      </nav>
      {footer && <div className="p-3 border-t border-border/50">{footer}</div>}
    </aside>
  );
}
